export interface ShareCycle {
  daysOn: number
  daysOff: number
}

export interface SharePattern {
  cycles: ShareCycle[]
  name?: string
}

export interface SharedRosterConfig {
  startDate: string
  monthsToShow: number
  pattern: SharePattern
}

const CYCLES_KEY = 'cycles'
const START_KEY = 'start'
const MONTHS_KEY = 'months'
const NAME_KEY = 'name'

/** Serialise a roster configuration into URL search parameters. */
export function buildShareQuery(config: SharedRosterConfig): string {
  const params = new URLSearchParams()
  params.set(START_KEY, config.startDate)
  params.set(MONTHS_KEY, String(config.monthsToShow))
  params.set(
    CYCLES_KEY,
    config.pattern.cycles.map((c) => `${c.daysOn}-${c.daysOff}`).join('-')
  )
  if (config.pattern.name) {
    params.set(NAME_KEY, config.pattern.name)
  }
  return params.toString()
}

/** Build an absolute shareable link for the current site. */
export function buildShareUrl(config: SharedRosterConfig): string {
  const { origin, pathname } = window.location
  return `${origin}${pathname}?${buildShareQuery(config)}#calculator`
}

function parseCycles(raw: string): ShareCycle[] | null {
  const numbers = raw
    .split('-')
    .map((part) => Number(part.trim()))
    .filter((n) => Number.isFinite(n) && n > 0)

  if (numbers.length < 2 || numbers.length % 2 !== 0) return null

  const cycles: ShareCycle[] = []
  for (let i = 0; i < numbers.length; i += 2) {
    cycles.push({ daysOn: numbers[i], daysOff: numbers[i + 1] })
  }
  return cycles
}

/** Read a shared roster configuration from a query string, or null if absent/invalid. */
export function parseSharedRoster(search: string): SharedRosterConfig | null {
  const params = new URLSearchParams(search)
  const cyclesRaw = params.get(CYCLES_KEY)
  if (!cyclesRaw) return null

  const cycles = parseCycles(cyclesRaw)
  if (!cycles) return null

  const startRaw = params.get(START_KEY)
  const startDate =
    startRaw && !Number.isNaN(new Date(startRaw).getTime())
      ? startRaw
      : new Date().toISOString().split('T')[0]

  const monthsParsed = Number(params.get(MONTHS_KEY))
  const monthsToShow = [1, 3, 6, 12].includes(monthsParsed) ? monthsParsed : 3

  const name = params.get(NAME_KEY) || undefined

  return { startDate, monthsToShow, pattern: { cycles, name } }
}

/** Human readable summary such as "8 on / 6 off, 7 on / 7 off". */
export function describeSharedPattern(pattern: SharePattern): string {
  return pattern.cycles
    .map((c) => `${c.daysOn} on / ${c.daysOff} off`)
    .join(', ')
}
